import * as React from "react";
import NextLink from "components/parts/NextLink";
import { MenuItems, SubNavMenuItem } from "../style";
import { Body, Heading } from "components/parts/Typography";
import { useTranslations } from "i18n";
import { FILES } from "i18n/constants";
import { useCountryStateCookies } from "hooks/cookies/useCountryStateCookies";
import { getMobileLinksByRegion } from "../../main-nav/navigation-links";
import type { MobileSubNavProps } from "../../main-nav/navigation-links";
import { NAV_LINK_KEYS, type NavLinkKey } from "../../main-nav/constants";

interface MobileSubMenuProps {
  menuType: NavLinkKey;
}

export const MobileSubMenu = ({
  menuType
}: MobileSubMenuProps): JSX.Element | null => {
  const t = useTranslations(FILES.COMMON);
  const { country } = useCountryStateCookies();

  // Get all mobile navigation links using the unified function
  const mobileLinks = getMobileLinksByRegion({
    t,
    countryCode: country
  });

  // Get the specific menu items based on menuType
  let menuData: MobileSubNavProps | null = null;
  switch (menuType) {
    case NAV_LINK_KEYS.CreditCard:
      menuData = mobileLinks.creditCard;
      break;
    case NAV_LINK_KEYS.Products:
      menuData = mobileLinks.products;
      break;
    case NAV_LINK_KEYS.Resources:
      menuData = mobileLinks.resources;
      break;
    case NAV_LINK_KEYS.Institutions:
      menuData = mobileLinks.institutions;
      break;
    default:
      return null;
  }

  if (!menuData || !menuData.items || menuData.items.length === 0) {
    return null;
  }

  return (
    <div>
      <MenuItems>
        {menuData.items.map(
          (
            item: {
              url?: string;
              name?: string;
              newTab?: boolean;
              title?: string;
              useReferrerPolicy?: boolean;
            },
            idx: number
          ) => {
            // Check if this is a header item (has title but no url or name)
            const isHeader = !item.url && !item.name && item.title;

            if (isHeader) {
              // Section header
              return (
                <div
                  key={`header-${idx}`}
                  style={{
                    margin: "24px 0 8px 0",
                    paddingTop: idx === 0 ? "0" : "16px"
                  }}
                >
                  <Heading
                    size="sm"
                    m="0"
                    color="rgba(255, 255, 255, 0.6)"
                    textTransform="uppercase"
                  >
                    {item.title}
                  </Heading>
                </div>
              );
            }

            // Regular menu item
            return (
              <SubNavMenuItem key={`navItem-${idx}`}>
                <Body size="lg" m="0" color="white" semiBold>
                  {item?.url ? (
                    <NextLink
                      href={item.url}
                      newTab={item.newTab}
                      useReferrerPolicy={item.useReferrerPolicy}
                      prefetch={false}
                    >
                      {item.name}
                    </NextLink>
                  ) : (
                    item?.name
                  )}
                </Body>
              </SubNavMenuItem>
            );
          }
        )}
      </MenuItems>
    </div>
  );
};

export default MobileSubMenu;
